import {
  COLLABORATE_EXTERNAL_KEYS,
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_STATUS,
  EXPIRED_TRIAL_MESSAGE_MAX_ACTIVE_DAYS,
  AUTODESK_PAY_CLIENT_IDS,
  AUTODESK_PAY_FORGE_CLIENT_ID,
  AUTODESK_PAY_SCRIPT_ELEMENT_ID,
} from './const';
import { User, Subscription, SubscriptionConstruction, WindowWithSDK } from './types';

export const isPaidSubscription = (
  subscription: SubscriptionConstruction,
  subscriptions: SubscriptionConstruction[],
) =>
  subscriptions.some(
    (current) =>
      !!current.offeringName &&
      current.offeringName === subscription.offeringName &&
      current.status === SUBSCRIPTION_STATUS.ACTIVE &&
      current.type !== SUBSCRIPTION_TYPES.TRIAL,
  );

const camelize = (str: string) => {
  return str
    .toLowerCase()
    .replace(/[^a-z\d]+(.)/gi, (m, chr) => chr.toUpperCase());
};

const generateProductName = (
  trial: Subscription | SubscriptionConstruction,
) => {
  const productName =
    'offeringCategoryName' in trial
      ? trial.offeringCategoryName
      : trial.offeringName;

  if (
    'offeringCategory' in trial &&
    trial.offeringExternalKey === COLLABORATE_EXTERNAL_KEYS.COLLABORATE_PRO
  ) {
    return productName + ' Pro';
  }
  return productName;
};

const reducer = (
  result: { [key: string]: string | number },
  trial: Subscription | SubscriptionConstruction,
  index: number,
) => {
  result.trialsDisplayed = `${result.trialsDisplayed}${
    index !== 0 ? ', ' : ''
  }${generateProductName(trial)}`;
  result[`${camelize(generateProductName(trial))}TrialDaysRemaining`] =
    trial.daysRemaining;
  return result;
};

const generateTrialsTrackingData = (
  trials: Subscription[] | SubscriptionConstruction[],
) => {
  return trials.reduce(reducer, { trialsDisplayed: '' });
};

export const getTrackingProperties = (
  user: User | null,
  trials: Subscription[] | SubscriptionConstruction[],
  currentProductId: string,
) => ({
  modalOpenTimeStamp: new Date(Date.now()).toISOString(),
  currentPage: currentProductId,
  currentUserEmailDomain:
    user && user.email.substring(user.email.lastIndexOf('@') + 1),
  ...generateTrialsTrackingData(trials),
});

export const addDaysToDate = (date: Date, days: number) => {
  const result = new Date(date);
  return result.setDate(result.getDate() + days);
};

export const shouldShowExpiredMessage = (
  trials: SubscriptionConstruction[],
) => {
  const lastTrialExpired = trials.reduce(
    (result: SubscriptionConstruction, trial: SubscriptionConstruction) => {
      if (result) {
        return new Date(result.endDate) < new Date(trial.endDate)
          ? trial
          : result;
      }

      return trial;
    },
  );
  const trialBannerHideDate = addDaysToDate(
    new Date(lastTrialExpired.endDate),
    EXPIRED_TRIAL_MESSAGE_MAX_ACTIVE_DAYS,
  );
  const today = new Date(Date.now()).setHours(0, 0, 0, 0);

  return today <= trialBannerHideDate;
};

export const setupAutodeskPaySDK = (
  config: {
    env: 'QA' | 'STAGING' | 'PRODUCTION';
    getAccessToken: () => Promise<string>;
    scriptElementId?: string;
    onInitialized?: (success: boolean) => void;
  },
): (() => void) | void => {
  const scriptElementId = config.scriptElementId || AUTODESK_PAY_SCRIPT_ELEMENT_ID;
  let listenerAttached = false;

  const initializeSDK = () => {
    const sdk = (window as WindowWithSDK).AutodeskPaySDK;

    if (sdk === undefined || sdk.init === undefined) {
      console.warn('AutodeskPaySDK is not available');
      config.onInitialized?.(false);
      return;
    }

    sdk.init({
      clientId: AUTODESK_PAY_CLIENT_IDS[config.env],
      analytics: {forgeClientId: AUTODESK_PAY_FORGE_CLIENT_ID},
      accessTokenProvider: async function () {
        return await config.getAccessToken();
      },
    });

    console.log('Autodesk Pay initialized');
    config.onInitialized?.(true);
  };

  const scriptElem = document.getElementById(
    scriptElementId
  ) as HTMLScriptElement | null;

  if (scriptElem === null) {
    console.warn('Autodesk Pay SDK script element not found');
    config.onInitialized?.(false);
    return;
  }

  const sdk = (window as WindowWithSDK).AutodeskPaySDK;
  if (sdk?.init) {
    initializeSDK();
  } else {
    scriptElem.addEventListener('load', initializeSDK, { once: true });
    listenerAttached = true;
  }

  return () => {
    if (listenerAttached && scriptElem) {
      scriptElem.removeEventListener('load', initializeSDK);
    }
  };
};

export const handleCheckoutFlow = async (params: {
  trial?: Subscription | SubscriptionConstruction;
  language: string;
  onError?: (error: string) => void;
  onComplete?: () => void;
  onCancel?: () => void;
  onPopupInit?: (controls: { focus: () => void; close: () => void }) => void;
}) => {
  const { trial, language } = params;
  const sdk = (window as WindowWithSDK).AutodeskPaySDK;

  if (sdk === undefined || sdk.checkout === undefined) {
    params.onError?.('CHECKOUT_ERROR_GENERIC');
    return;
  }

  try {
    const { offeringPlc } =
      trial ?? {};
    const payload = {
      language,
      offers: [
        {
          offeringCode: offeringPlc,
          quantity: 1,
          termCode: 'A01',
        },
      ],
    };

    await sdk.checkout({
      payload: payload,
      onError: (error: { code: string }) => {
        if (error.code === 'POPUP_BLOCKED') {
          params.onError?.('CHECKOUT_ERROR_POPUP_BLOCKED');
        } else {
          params.onError?.('CHECKOUT_ERROR_GENERIC');
        }
      },
      onComplete: () => {
        params.onComplete?.();
      },
      onCancel: () => {
        params.onCancel?.();
      },
      onPopupInit: (context: { requestId: string, controls: { focus: () => void; close: () => void } }) => {
        const { controls } = context;
        params.onPopupInit?.({
          focus: () => controls.focus(),
          close: () => controls.close(),
        });
      },
    });
  } catch (error) {
    params.onError?.('CHECKOUT_ERROR_GENERIC');
  }
};

